import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Mail, AlertCircle, Loader, CheckCircle, ArrowLeft } from 'lucide-react'

function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  
  const { resetPassword } = useAuth()

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!email) {
      setError('Please enter your email address')
      return
    }
    
    try {
      setError('')
      setLoading(true)
      await resetPassword(email)
      setSuccess(true)
    } catch (err) {
      console.error('Password reset error:', err)
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email address')
      } else {
        setError('Failed to send reset email. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="auth-form">
        <div className="success-message">
          <div className="success-icon">
            <CheckCircle size={48} />
          </div>
          <h2>Check Your Email</h2>
          <p>
            We've sent a password reset link to <strong>{email}</strong>. 
            Please check your inbox and follow the instructions to reset your password.
          </p>
          <p className="hint">
            Don't see the email? Check your spam folder.
          </p>
          <Link to="/login" className="btn-primary">
            <ArrowLeft size={18} />
            <span>Back to Sign In</span>
          </Link>
        </div>

        <style>{`
          .success-message {
            text-align: center;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: var(--spacing-md);
          }
          
          .success-icon {
            width: 80px;
            height: 80px;
            background: rgba(124, 182, 95, 0.1);
            border-radius: var(--radius-full);
            display: flex;
            align-items: center;
            justify-content: center;
            color: var(--color-primary);
          }
          
          .success-message h2 {
            margin-bottom: 0;
          }
          
          .success-message p {
            color: var(--color-text-secondary);
            margin-bottom: 0;
          }
          
          .success-message .hint {
            font-size: 0.875rem;
            color: var(--color-text-muted);
          }
          
          .success-message .btn-primary {
            margin-top: var(--spacing-md);
            text-decoration: none;
          }
        `}</style>
      </div>
    )
  }

  return (
    <form className="auth-form" onSubmit={handleSubmit}>
      <div className="auth-form-header">
        <h2>Reset Password</h2>
        <p>Enter your email and we'll send you a reset link</p>
      </div>

      {error && (
        <div className="form-error">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="email">Email Address</label>
        <div className="input-with-icon">
          <Mail size={18} className="icon" />
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            required
          />
        </div>
      </div>

      <button type="submit" className="btn-primary" disabled={loading}>
        {loading ? (
          <>
            <Loader size={18} className="spinner" />
            <span>Sending...</span>
          </>
        ) : (
          <span>Send Reset Link</span>
        )}
      </button>

      <div className="auth-links">
        <Link to="/login">
          <ArrowLeft size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
          Back to Sign In
        </Link>
      </div>
    </form>
  )
}

export default ForgotPasswordPage
